import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Loading,
  Modal,
  StatCard,
} from '@/components/ui';

/**
 * Who is actually on the dialer, asked of the dialer.
 *
 * Not inferred from this platform's records. A bot can only hand a call to
 * somebody who is logged in and ready, so "why are my transfers failing" is
 * usually answered here and nowhere else — and a client previously had no way
 * to see it without opening Vicidial's own admin, which most of them do not
 * have access to.
 *
 * WHY IT IS FOLDED AWAY BY DEFAULT
 * --------------------------------
 * It was not, and on a ten-agent account it took the entire first screen of the
 * Live calls page: three large cards and ten table rows before a single live
 * call. Reported exactly that way — "I am not able to see live calls".
 *
 * The three numbers are the answer almost every time. The per-agent table is
 * for the case where they do not add up, and it is one click away.
 *
 * Polled rather than pushed. Agent status changes every few seconds and is read
 * off a dialer over HTTP, one request per agent; a live socket for it would
 * cost far more than it is worth for a number nobody watches continuously.
 */

interface Agent {
  vicidial_user: string;
  name: string | null;
  dialer: string | null;
  campaign: string | null;
  in_group: string | null;
  status: string;
  raw_status: string | null;
  detail: string | null;
}

interface Response {
  data: Agent[];
  configured: number;
  logged_in: number;
  on_call: number;
  note?: string;
  /** Distinct reasons the dialer gave, with how many agents each covers. */
  reasons?: Array<{ reason: string; agents: number }>;
}

const TONE: Record<string, 'green' | 'amber' | 'gray' | 'red'> = {
  'ON CALL': 'green',
  READY: 'green',
  PAUSED: 'amber',
  // A definite "they are not there", which is a different thing from the
  // dialer not answering at all — and the bot now treats them differently too.
  'NOT LOGGED IN': 'red',
  UNKNOWN: 'gray',
};

export function LiveAgents({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['live-agents'],
    queryFn: () => api.get<Response>('/agents/live'),
    refetchInterval: 15_000,
  });

  if (isPending) return <Loading label="Asking the dialer…" />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  if (!data.data.length) {
    return (
      <Card title="Agents">
        <p className="text-sm text-ink-500">
          {data.note ?? 'No Vicidial agents are configured for this account.'}
        </p>
      </Card>
    );
  }

  /**
   * The dialer answered for nobody at all.
   *
   * Ten rows saying "unknown" repeat what "0 logged in" already said, ten
   * times, and push everything else off the screen. It is one fact, so it gets
   * one line — and it is a fact with two quite different causes, which the
   * line has to name because this platform genuinely cannot tell them apart.
   */
  const allUnknown = data.logged_in === 0 && data.configured > 0;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Agents configured" value={String(data.configured)} dense />
        <StatCard
          label="Logged in"
          value={String(data.logged_in)}
          tone={allUnknown ? 'warn' : 'default'}
          sub={
            data.logged_in < data.configured
              ? `${data.configured - data.logged_in} not on the dialer`
              : 'all of them'
          }
          dense
        />
        <StatCard
          label="On a call"
          value={String(data.on_call)}
          sub={`${Math.max(data.logged_in - data.on_call, 0)} free to take one`}
          dense
        />
      </div>

      {allUnknown && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>The dialer did not answer for any of these agents.</strong>{' '}
          Usually that means nobody is logged in — but it looks identical to the
          dialer refusing the status call, and this platform cannot tell the two
          apart. If somebody <em>is</em> logged in and this still says zero,
          check the API user&apos;s permissions on the Vicidial server.
          <br />
          Either way, a bot cannot transfer to an agent who is not there.
          {!!data.reasons?.length && (
            <ul className="mt-1.5 space-y-0.5 font-mono text-2xs">
              {data.reasons.map((row) => (
                <li key={row.reason}>
                  {row.reason}
                  <span className="ml-1.5 font-sans opacity-70">
                    ({row.agents} agent{row.agents === 1 ? '' : 's'})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!compact && (
        <>
          <div className="mt-2">
            <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
              Agent details ({data.configured})
            </Button>
          </div>

          {/* A dialog rather than an expanding panel. The panel pushed the
              calls down the page the moment it opened, which is the problem
              this component had in the first place -- somebody checking on
              their agents should not lose sight of the calls to do it. */}
          <Modal
            open={open}
            onClose={() => setOpen(false)}
            wide
            title="Agents"
            description="Read from the dialer, not from this platform's own records."
          >
            <div className="overflow-x-auto">
                <table className="glee-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Dialer</th>
                      <th>Campaign</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((agent) => (
                      <tr key={`${agent.dialer ?? ''}-${agent.vicidial_user}`}>
                        <td>
                          <div className="font-medium">
                            {agent.name || agent.vicidial_user}
                          </div>
                          <div className="font-mono text-2xs text-ink-500">
                            {agent.vicidial_user}
                          </div>
                        </td>
                        <td className="text-xs text-ink-500">
                          {agent.dialer ?? '—'}
                        </td>
                        <td className="text-xs text-ink-500">
                          {agent.campaign ?? '—'}
                          {agent.in_group && ` · ${agent.in_group}`}
                        </td>
                        <td>
                          <Badge tone={TONE[agent.status] ?? 'gray'}>
                            {agent.status.toLowerCase()}
                          </Badge>
                          {agent.detail && (
                            <div className="mt-0.5 font-mono text-2xs text-ink-500">
                              {agent.detail}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-3 text-2xs text-ink-500">
                <strong>Not logged in</strong> is the dialer saying so. <strong>
                Unknown</strong> is the dialer not answering at all — which the
                bot treats differently: it will still attempt a transfer,
                because a dialer that cannot be reached must not stop every
                transfer on the platform.
              </p>
          </Modal>
        </>
      )}
    </div>
  );
}
